<script setup>
import { i18n } from 'boot/i18n';
import {
  HrCurrencyField,
  HrList,
  HrListAddBtn,
  HrListItem,
  HrListSectionHeader,
  HrOverflowMenuBtn,
  HrP,
  HrVersionedFormDialog,
  HrWarningBanner,
  confirmDestructiveDialog,
  notifyInfo,
  notifyNegative,
  useDialog,
} from 'src/design-system';
import { computed, onMounted, ref } from 'vue';

import { region as useRegionStore } from 'src/store/region';
import { formatCalendarDate } from 'src/utils/DateUtils';

import {
  CURRENCY,
  DEFAULT_CURRENCY_BY_REGION,
  fromMinorUnits,
  isChargeMinorUnits,
  toMinorUnits,
} from '../../domain/constants/currency';
import { unchargedCodeIds } from '../../domain/services/chargeMasterResolution';
import {
  SERVICE_CODE_CHANGE_REJECTION,
  serviceCodeRemovalRejection,
} from '../../domain/services/serviceCodeGovernance';
import { useChargeMaster } from '../composables/useChargeMaster';
import { useServiceCodes } from '../composables/useServiceCodes';
import { chargeLineCaption } from '../presenters/chargeLineCaption';
import { presentChargeLineRows } from '../presenters/chargeLineRowPresenter';
import {
  effectiveDateFor,
  presentMinDate,
  presentReplacementWarning,
  presentSaveChoices,
  presentVersionLine,
} from '../presenters/fundingSourceVersionPresenter';
import RetireServiceCodeDialog from './RetireServiceCodeDialog.vue';
import ServiceCodePickerDialog from './ServiceCodePickerDialog.vue';

/**
 * Edit the clinic's charge master — which codes it bills on, and what it charges for each.
 *
 * THE ONLY INPUT ON THIS SHEET IS THE CHARGE. Code, descriptor and unit basis are read from the code
 * every time they are shown and can never be edited here: they are facts about a published code, not
 * about this clinic. The basis sits immediately beside the charge because a charge is not legible
 * without it — "120.00" says nothing, and "per 15 min" versus "per hour" is a fourfold difference.
 *
 * EDITS ARE STAGED AND COMMITTED AS ONE SHEET. Nothing is written until the dialog's second step
 * asks from when the change applies, because for a record the rest of the system resolves by date,
 * "when" is part of the edit. That is also why there is no per-row save: a sheet is one decision
 * about what the clinic charges, not a pile of independent ones.
 *
 * THE SAVE-DATE RULES ARE THE FUNDER DIALOG'S, reused rather than reimplemented. Which dates a new
 * version may take, what a correction supersedes and how far back the picker may go are functions of
 * the timeline alone; only the wording differs, which is what the key prefix carries.
 *
 * UNCHARGED LINES WARN AND NEVER BLOCK. A clinic mid-setup with codes on the sheet and no charges yet
 * has made no mistake; those lines hold at billing, visibly, with a reason. Refusing the save would
 * turn an ordinary intermediate state into a failure.
 */
const props = defineProps({
  /** The clinic whose charge master is being edited. */
  clinicId: {
    type: String,
    required: true,
  },
});

const emit = defineEmits({
  /** Fired after a sheet is written. */
  ok: null,
  /** Fired when the dialog is dismissed without saving. */
  hide: null,
});

const KEY_PREFIX = 'settings.billing.chargeMaster.versions';

/**
 * The dialog root, and the reason `defineExpose` below is not optional.
 *
 * Quasar's `Dialog.create({ component })` drives the component through `show()` and `hide()`, so a
 * component opened that way MUST expose both — without them it is rejected outright with
 * "Incorrectly defined Dialog component" and never renders. It is not something the design system
 * can supply on our behalf: the plugin calls the methods on the component it was handed, not on
 * whatever that component happens to render.
 */
const dialog = ref(null);

function hide() {
  dialog.value?.hide();
}

const chargeMaster = useChargeMaster();
const codes = useServiceCodes({ asOf: chargeMaster.asOf });
const { openComponentDialog } = useDialog();

const saveChoice = ref(null);
const pickedDate = ref(null);
const isSaving = ref(false);

/** The sheet being edited, as a staged copy. Nothing here touches storage until the save. */
const draftLines = ref([]);
const draftCurrency = ref(null);

/**
 * What is TYPED in each charge field, keyed by code id — deliberately separate from `chargeMinor`.
 *
 * TWO REPRESENTATIONS, AND THEY MUST NOT BE DERIVED FROM EACH OTHER WHILE TYPING. `chargeMinor` is
 * what gets saved; this is what the user is in the middle of writing. Binding the field to a value
 * re-derived from `chargeMinor` is a bug with teeth: typing `1` stores 100, which renders back as
 * `1.00`, which moves the caret to the end — and the next digit produces `1.001`, which has more
 * decimals than the currency allows, so it converts to null and the charge silently disappears.
 *
 * So the field shows this, and `chargeMinor` follows from it. They meet only at load and at save.
 */
const chargeText = ref({});

/**
 * Where a draft line came from, which is what decides what REMOVING it means.
 *
 * A line off the committed sheet can be marked and restored — the row stays on screen struck
 * through, so the sheet shows the diff against what is in force while the user is still deciding. A
 * line added in this session has no committed state to diff against, so a pending state would mean
 * nothing; it is discarded outright with an undo instead.
 *
 * Editor state only. Neither value reaches storage: marked lines are dropped before the write, and
 * the mapper carries only `serviceCodeId` and `chargeMinor` regardless.
 */
const ORIGIN = { COMMITTED: 'committed', SESSION: 'session' };

/**
 * Codes this session MINTED, as opposed to merely added to the sheet.
 *
 * THE TWO ARE NOT THE SAME AND THE DIFFERENCE DECIDES WHETHER A CODE CAN BE REMOVED. Adding a code
 * stages a line; minting one creates a record in `serviceCodes` immediately, before anything is
 * saved. That record is the only thing on this screen that exists outside the staging, and it is
 * removable exactly while nothing can have referenced it yet — which is this window and no other.
 *
 * IT IS ALSO A CLEANUP LIST. A code minted here and then abandoned — the line discarded, or the
 * dialog closed without saving — would otherwise sit in the clinic's code list forever, unusable and
 * un-removable, because the moment this dialog closes it stops being session-minted. So closing
 * tombstones whatever is still in here.
 */
const sessionMintedCodeIds = ref([]);

/**
 * The region this session is connected to — the seed for a first sheet's currency.
 *
 * THE LIVE REGION, NEVER `defaultRegion`. That export is a build-time fallback — `'us'` in
 * production, `'eu'` everywhere else — and is not the region the user is on: `updateFirebaseConnection`
 * resolves the real one from the URL, then local storage, and only falls back to it. Seeding from it
 * gave every production clinic USD, so a Canadian or Australian one silently priced its whole sheet in
 * the wrong currency. This store is synced to that same local-storage value at boot, so it agrees with
 * the database the sheet is being written to.
 *
 * IT MATTERS MORE THAN A PREFILL USUALLY WOULD, because a series' currency cannot be changed
 * afterwards — `CurrencyChangeRejected` refuses it — and there is no control on this dialog to correct
 * it before the first save. Getting the seed wrong is therefore not a nuisance the user can fix; see
 * the review note on making the currency an explicit choice.
 */
const regionStore = useRegionStore();

const loadDraft = () => {
  const sheet = chargeMaster.scheduled.value ?? chargeMaster.inForce.value;
  const currency = sheet?.currency
    ?? DEFAULT_CURRENCY_BY_REGION[regionStore.region]
    ?? CURRENCY.USD;

  draftLines.value = (sheet?.lines ?? []).map((line) => ({
    ...line,
    origin: ORIGIN.COMMITTED,
    isPendingRemoval: false,
  }));
  draftCurrency.value = currency;
  chargeText.value = Object.fromEntries(draftLines.value.map((line) => ([
    line.serviceCodeId,
    fromMinorUnits(line.chargeMinor, currency) ?? '',
  ])));
};

/**
 * A FAILED READ CLOSES THE DIALOG rather than leaving it open, and that is the one case where closing
 * is kinder than staying. `isLoading` is derived from `hasLoaded` on both composables, so a rejected
 * read leaves the skeleton rows on screen for good — a dialog that never resolves, with a Save button
 * that would write an empty sheet over the clinic's charges if it were pressed.
 */
onMounted(async () => {
  try {
    await Promise.all([
      chargeMaster.load({ clinicId: props.clinicId }),
      codes.load({ clinicId: props.clinicId }),
    ]);
    loadDraft();
  } catch (error) {
    notifyNegative(i18n.t('settings.billing.chargeMaster.notifyLoadFailed'));
    console.error('Failed to load the charge master', error);
    hide();
  }
});

/** The entry being edited: a scheduled sheet if one exists, else what is in force. */
const selected = computed(() => (
  chargeMaster.scheduledEntries.value[0] ?? chargeMaster.inForceEntry.value
));

const versionLine = computed(() => presentVersionLine(chargeMaster.timeline.value, {
  asOf: chargeMaster.asOf,
  selected: selected.value,
  keyPrefix: KEY_PREFIX,
}));

const saveChoices = computed(() => presentSaveChoices(chargeMaster.timeline.value, {
  asOf: chargeMaster.asOf,
  selected: selected.value,
  keyPrefix: KEY_PREFIX,
}));

const effectiveFrom = computed(() => effectiveDateFor(saveChoice.value, {
  asOf: chargeMaster.asOf,
  selected: selected.value,
  pickedDate: pickedDate.value,
}));

const replacementWarning = computed(() => presentReplacementWarning(chargeMaster.timeline.value, {
  asOf: chargeMaster.asOf,
  effectiveFrom: effectiveFrom.value,
  keyPrefix: KEY_PREFIX,
}));

/** Rows for the sheet as staged, so an added code appears before anything is written. */
const rows = computed(() => presentChargeLineRows(
  { currency: draftCurrency.value, lines: draftLines.value },
  codes.codeById,
  { asOf: chargeMaster.asOf },
));

const onSheetCodeIds = computed(() => draftLines.value.map((line) => line.serviceCodeId));

const pendingRemovalCodeIds = computed(() => draftLines.value
  .filter((line) => line.isPendingRemoval)
  .map((line) => line.serviceCodeId));

/**
 * What the save actually writes: everything not marked for removal, stripped back to a charge line.
 *
 * MARKED LINES ARE DROPPED HERE and nowhere else. That absence IS the removal — the model says "this
 * line ended" by the next sheet not listing it, so nothing needs a tombstone or an end date. The
 * mapper would happily persist a marked line, since it carries only `serviceCodeId` and
 * `chargeMinor` and knows nothing of editor state, so the filtering has to happen before it.
 */
const linesToSave = computed(() => draftLines.value
  .filter((line) => !line.isPendingRemoval)
  .map(({ serviceCodeId, chargeMinor }) => ({ serviceCodeId, chargeMinor })));

/**
 * Codes whose field holds text that is not an amount — one decimal too many, usually.
 *
 * DISTINCT FROM UNCHARGED, AND THE DISTINCTION IS THE WHOLE POINT. An empty charge is a decision the
 * clinic is allowed to make and save; text that does not convert is an unfinished edit. Saving it
 * would store `null` and report the row as "needs a charge" — silently discarding a number the user
 * typed and believes they entered. That is the one thing this dialog must not do.
 *
 * So this refuses the SAVE while leaving the row editable, which is a different guard from the
 * uncharged warning below and must not be collapsed into it.
 */
const invalidCharges = computed(() => draftLines.value
  .filter((line) => !line.isPendingRemoval
    && (chargeText.value[line.serviceCodeId] ?? '') !== ''
    && !isChargeMinorUnits(line.chargeMinor))
  .map((line) => codes.codeById(line.serviceCodeId)?.code ?? line.serviceCodeId));

const invalidWarning = computed(() => (invalidCharges.value.length === 0
  ? null
  : i18n.t('settings.billing.chargeMaster.warningInvalidCharges', {
    codes: invalidCharges.value.join(', '),
  })));

/**
 * The codes that will hold at billing if this is saved as it stands.
 *
 * Named rather than counted. "Three codes have no charge" tells somebody there is a problem; naming
 * them tells them where it is.
 */
const willHoldAtBilling = computed(() => unchargedCodeIds({ lines: linesToSave.value })
  .map((serviceCodeId) => codes.codeById(serviceCodeId)?.code ?? serviceCodeId));

const unchargedWarning = computed(() => (willHoldAtBilling.value.length === 0
  ? null
  : i18n.t('settings.billing.chargeMaster.warningUncharged', {
    codes: willHoldAtBilling.value.join(', '),
  })));

/**
 * Typing a charge.
 *
 * REFUSES WHAT IT CANNOT REPRESENT rather than rounding it. `toMinorUnits` returns null for more
 * decimal places than the currency has, so the row simply stays uncharged instead of quietly
 * becoming a number nobody typed.
 */
const setCharge = (serviceCodeId, amount) => {
  const line = draftLines.value.find((entry) => entry.serviceCodeId === serviceCodeId);

  if (line === undefined) {
    return;
  }
  // The typed text is kept verbatim; the stored value follows from it. Never the other way round —
  // see `chargeText` for what happens to the caret if these are derived from each other.
  chargeText.value = { ...chargeText.value, [serviceCodeId]: `${amount ?? ''}` };
  line.chargeMinor = toMinorUnits(amount, draftCurrency.value);
};

/**
 * Rewrites the field to how the amount is WRITTEN, once the user has finished typing.
 *
 * ON BLUR AND NEVER BEFORE. While the field has focus its text is left exactly as typed — that is
 * what keeps the caret still and what lets `2.` exist on the way to `2.20`. Formatting there would
 * reintroduce the caret jump the two representations exist to prevent. Once focus leaves there is no
 * caret to disturb, and `2.2` should read as `2.20` because that is how a charge is written.
 *
 * UNREADABLE TEXT IS LEFT ALONE. There is nothing to normalize it to, and replacing it would
 * discard what the user typed at exactly the moment they need to see it to fix it.
 */
const normalizeCharge = (serviceCodeId) => {
  const line = draftLines.value.find((entry) => entry.serviceCodeId === serviceCodeId);
  const written = fromMinorUnits(line?.chargeMinor, draftCurrency.value);

  if (written !== null) {
    chargeText.value = { ...chargeText.value, [serviceCodeId]: written };
  }
};

/**
 * The row being edited, marked with M3's active indicator.
 *
 * ACTIVE RATHER THAN A FOCUS RING, because the problem is finding the row, not finding the field.
 * With ~600px between a descriptor and its charge, the focus ring on the field says which control
 * has focus but not which line it belongs to — the eye still has to track back across the gap. The
 * active indicator tints the whole row, so the answer is the row itself.
 *
 * `active` IS INDEPENDENT OF `clickable`, which matters: the row gains a visual state without
 * becoming a click target, so the no-nested-interactive-targets rule is untouched.
 *
 * Null rather than false when inactive, matching the prop's documented contract — null means "no
 * active style", which is not quite the same as asserting the row is inactive.
 */
const focusedCodeId = ref(null);

/**
 * Leaving a charge field: normalize what was typed, and give up the active row ONLY if it is still
 * ours.
 *
 * THE GUARD IS A FOCUS/BLUR RACE, not defensiveness. Clicking from one field straight to another
 * fires two events, and their order is not guaranteed — the incoming `focus` can land before the
 * outgoing `blur`. Clearing unconditionally then wipes the id that was just set, and the row you
 * moved to never lights up. That is the "sometimes it does not become active" case: it depends
 * entirely on which event won, so it looks intermittent rather than wrong.
 *
 * Comparing first makes the order irrelevant — a late blur from the field we already left cannot
 * unset the field we are now in.
 */
const onChargeBlur = (serviceCodeId) => {
  if (focusedCodeId.value === serviceCodeId) {
    focusedCodeId.value = null;
  }
  normalizeCharge(serviceCodeId);
};

/**
 * The skeleton rows shown while the sheet and the catalog are read.
 *
 * `HrP` sizes its skeleton from the text it is standing in for, so these strings are widths rather
 * than content — a code is short, a descriptor is long, and the placeholder should look like the row
 * it is about to become. Three rows suggests a list without implying how many are coming.
 */
const SKELETON_ROWS = [1, 2, 3];
const SKELETON_CODE_WIDTH = '00000';
const SKELETON_DESCRIPTOR_WIDTH = 'Behavior treatment for severe destructive behavior';

/**
 * Both reads have to finish before a row can be rendered honestly.
 *
 * A ROW NEEDS BOTH. The sheet supplies the lines and their charges; the catalog supplies each
 * code's string, descriptor and unit basis. With only the first, every row renders as an unknown code
 * with no basis — which looks like a sheet full of dangling references rather than a screen that has
 * not finished loading.
 *
 * AND AN EMPTY LIST MUST NOT SPEAK YET. Before the reads land there are no rows, so a subhead reading
 * "0 codes" beside an Add button would tell a clinic its charge master is empty when we simply have
 * not looked. That is the same "not read yet is not nothing there" distinction the treatment-service
 * picker turns on, and it is worth the extra state here for the same reason.
 */
const isLoading = computed(() => !chargeMaster.hasLoaded.value || !codes.hasLoaded.value);

/** The list element, so Enter can reach the next row's field without a ref per row. */
const listEl = ref(null);

/**
 * Enter moves to the next row's charge field — spreadsheet order, without leaving the keyboard.
 *
 * ENTRY IS THE LABOR ON THIS SCREEN, not scanning. Nobody types thirty charges happily if each one
 * costs a reach for the mouse, so Enter walks the column. It is the difference between thirty rows
 * being tolerable and a hundred and fifty being possible.
 *
 * FOUND THROUGH THE DOM RATHER THAN A REF PER ROW, because the rows are a `v-for` over a computed and
 * a ref array would have to be kept in step with it by hand — one more thing to get wrong when rows
 * are added, discarded or restored. The lookup is keyed on the code id the row already carries.
 *
 * THE LAST ROW KEEPS FOCUS rather than wrapping to the first. Wrapping would silently send someone
 * who has finished back to the top, and the next thing they typed would land on a charge they had
 * already entered — with nothing on screen to say the cursor had moved.
 */
const focusNextCharge = (serviceCodeId) => {
  const index = rows.value.findIndex((row) => row.serviceCodeId === serviceCodeId);
  const next = rows.value[index + 1];

  if (next === undefined) {
    return;
  }
  listEl.value
    ?.$el
    ?.querySelector(`[data-charge-field="${next.serviceCodeId}"] input`)
    ?.focus();
};

/**
 * Is there anything in this field, whatever it converts to?
 *
 * DELIBERATELY NOT `hasCharge`. It answers "is there text here", not "is that text any good", which
 * is what lets the hint below tell an empty field apart from one holding something unreadable.
 */
const hasTypedCharge = (serviceCodeId) => (chargeText.value[serviceCodeId] ?? '') !== '';

/**
 * What to say under a charge field — and for an unpriced one, deliberately nothing.
 *
 * THE "NEEDS A CHARGE" MESSAGE IS GONE FROM THE ROW. Repeated down a long sheet it was lumpy
 * vertical texture carrying no information: priced rows short, unpriced rows tall, and the field
 * itself drifting off the row's text center on exactly the rows that needed attention. The condition
 * is now said by the field's own placeholder, and its consequence once, in the subhead, where it is
 * visible without scrolling.
 *
 * AN UNCONVERTIBLE AMOUNT STILL SPEAKS UP, because it is a different situation and a rare, transient
 * one. `12.244` is somebody halfway through a mistake and needs to be told so where they are typing;
 * an empty field is an ordinary mid-setup state and needs no interruption at all.
 */
const chargeHint = (row) => (hasTypedCharge(row.serviceCodeId) && !row.hasCharge
  ? i18n.t('settings.billing.chargeMaster.hintInvalidCharge')
  : null);

/**
 * Removes the LINE, never the code — the catalog record is untouched either way.
 *
 * TWO CASES, AND THE DIFFERENCE IS WHETHER THERE IS ANYTHING TO DIFF AGAINST. A line off the
 * committed sheet is MARKED: it stays on screen struck through, its charge preserved, one click from
 * being restored — which is also what puts the diff in front of the user before they commit to it. A
 * line added in this session is DISCARDED, because a pending state against nothing is meaningless;
 * an undo snackbar covers the mis-click, proportionate to what was lost.
 *
 * NO CONFIRMATION EITHER WAY. Nothing is written until the sheet is saved, so a modal would tax
 * every removal to guard against the occasional slip. What was missing is undo, not friction.
 */
const removeLine = (serviceCodeId) => {
  const line = draftLines.value.find((entry) => entry.serviceCodeId === serviceCodeId);

  if (line === undefined) {
    return;
  }
  if (line.origin === ORIGIN.COMMITTED) {
    // Marked, not dropped — and the charge is left exactly as it stands, so restoring returns an
    // edit made before the removal rather than silently reverting it.
    line.isPendingRemoval = true;
    return;
  }
  const discarded = { ...line };
  const discardedText = chargeText.value[serviceCodeId] ?? '';

  draftLines.value = draftLines.value.filter((entry) => entry.serviceCodeId !== serviceCodeId);
  notifyInfo(
    i18n.t('settings.billing.chargeMaster.notifyLineDiscarded', {
      code: codes.codeById(serviceCodeId)?.code ?? serviceCodeId,
    }),
    {
      actions: [{
        label: i18n.t('settings.billing.chargeMaster.buttonUndo'),
        handler: () => {
          draftLines.value = draftLines.value.concat([discarded]);
          chargeText.value = { ...chargeText.value, [serviceCodeId]: discardedText };
        },
      }],
    },
  );
};

/** Puts a marked line back, charge and all. */
const restoreLine = (serviceCodeId) => {
  const line = draftLines.value.find((entry) => entry.serviceCodeId === serviceCodeId);

  if (line !== undefined) {
    line.isPendingRemoval = false;
  }
};

/**
 * The row a code lives on, whatever state it is in. Null when the sheet has no line for it.
 *
 * MARKED LINES COUNT AS PRESENT, which is the whole reason this looks past `isPendingRemoval`. A
 * code marked for removal still occupies its one place on the sheet; adding it again must bring that
 * row back rather than mint a second one, or the same code appears twice in conflicting states and
 * needs duplicate handling that should not have to exist.
 */
const lineFor = (serviceCodeId) => draftLines.value.find(
  (line) => line.serviceCodeId === serviceCodeId,
) ?? null;

/**
 * Adding a code — ONE CODE, ONE ROW, whatever state that row is in.
 *
 * The picker only returns a code that has something to do here: absent from the sheet, or on it and
 * marked for removal. A code already present and staying is listed but not selectable, and says why,
 * so this is never asked to make selecting one mean nothing.
 *
 * MARKED IS RESTORED RATHER THAN APPENDED, which is why the lookup ignores `isPendingRemoval`. A
 * marked line still occupies the code's one place on the sheet; appending a second would put the same
 * code in two conflicting states and invent a duplicate problem the model has no notion of. Restoring
 * also returns the charge as it stood, so an edit made before the removal is not quietly reverted.
 *
 * A NEW LINE GOES AT THE END, so the row a user just added is where they are already looking.
 */
const addCode = () => {
  return openComponentDialog(
    ServiceCodePickerDialog,
    {
      clinicId: props.clinicId,
      onSheetCodeIds: onSheetCodeIds.value,
      pendingRemovalCodeIds: pendingRemovalCodeIds.value,
    },
    async ({ serviceCodeId, isMinted } = {}) => {
      if (typeof serviceCodeId !== 'string') {
        return;
      }
      /*
       * RECORDED BEFORE THE RE-READ, and never derived from what the re-read returns. The mint is
       * already written by the time the picker closes, so the one thing that has to survive a failed
       * read is knowing the code is ours to clean up. Deriving it from the catalog made the two fail
       * together: a read that failed left the code looking like nobody's, so the cleanup on close
       * skipped it, its row offered neither retire nor delete, and no route to remove it remained.
       */
      if (isMinted === true) {
        sessionMintedCodeIds.value = sessionMintedCodeIds.value.concat([serviceCodeId]);
      }
      // THE CATALOG IS RE-READ BEFORE THE ROW APPEARS, because the picker may have MINTED the code
      // rather than found it. Our copy predates that write, so the row would render as an unknown
      // code with no descriptor and no basis — indistinguishable from a dangling reference — until
      // something else happened to reload. Re-reading also picks up whatever a colleague landed.
      await codes.load({ clinicId: props.clinicId }).catch((error) => {
        console.error('Failed to re-read service codes after adding one', error);
      });
      const existing = lineFor(serviceCodeId);

      if (existing !== null) {
        existing.isPendingRemoval = false;
        return;
      }
      draftLines.value = draftLines.value.concat([{
        serviceCodeId,
        chargeMinor: null,
        origin: ORIGIN.SESSION,
        isPendingRemoval: false,
      }]);
      chargeText.value = { ...chargeText.value, [serviceCodeId]: '' };
    },
  );
};

/**
 * Every code id any SAVED sheet of this clinic mentions, tombstoned sheets included.
 *
 * THE EVIDENCE BEHIND "REFERENCED", and it counts a sheet that was canceled before its date
 * arrived. A tombstoned sheet is a decision that was recorded and withdrawn, not a document that
 * never existed, and a code named on one has been referenced in the only sense that matters here —
 * something outside this dialog once pointed at it. Erring wide is the safe direction: the cost of
 * over-counting is a code that can only be retired, and the cost of under-counting is a removal that
 * orphans a reference permanently.
 */
const committedCodeIds = computed(() => new Set(
  chargeMaster.sheets.value.flatMap((sheet) => (sheet.lines ?? []).map(
    (line) => line.serviceCodeId,
  )),
));

/**
 * Why this code cannot be removed, or null when it can.
 *
 * ASKED OF THE DOMAIN RULE rather than re-derived here, so the menu and the write agree. The rule is
 * deliberately narrow — removable only while minted in this session and unreferenced — and the menu
 * shows the refusal in place rather than hiding the item, because a control that disappears sends
 * somebody looking for it again.
 */
const CANNOT_DELETE_KEY = {
  [SERVICE_CODE_CHANGE_REJECTION.REFERENCED]:
    'settings.billing.chargeMaster.menu.cannotDeleteReferenced',
};

const removalRejectionFor = (serviceCodeId) => serviceCodeRemovalRejection({
  serviceCode: codes.codeById(serviceCodeId),
  isSessionMinted: sessionMintedCodeIds.value.includes(serviceCodeId),
  // ONLY THE HALF THIS SCREEN CAN SEE. The treatment-service reference is read by the use case at
  // the moment of the write; the menu cannot afford a query per row. The two cannot disagree today —
  // a session-minted code is on no saved sheet, so the treatment-service picker never offered it —
  // and when the gate widens, the WRITE is the one that has to be right.
  hasCommittedReference: committedCodeIds.value.has(serviceCodeId),
});

/**
 * Why a retirement was refused, in words. Falls back to the generic failure for anything unmapped,
 * because a message naming no reason still beats one naming the wrong one.
 *
 * `ENDS_BEFORE_START` is reachable only when the code's own start date moved under an open dialog:
 * the calendar's lower bound refuses it otherwise. It is mapped anyway — the write is the authority,
 * and a guard that has been bypassed is exactly when the message has to explain itself.
 */
const RETIRE_REJECTION_KEY = {
  [SERVICE_CODE_CHANGE_REJECTION.ENDS_BEFORE_START]:
    'settings.billing.chargeMaster.retire.errorEndsBeforeStart',
  [SERVICE_CODE_CHANGE_REJECTION.MANAGED]:
    'settings.billing.chargeMaster.retire.errorManaged',
  [SERVICE_CODE_CHANGE_REJECTION.NOT_FOUND]:
    'settings.billing.chargeMaster.retire.errorNotFound',
  [SERVICE_CODE_CHANGE_REJECTION.ALREADY_REMOVED]:
    'settings.billing.chargeMaster.retire.errorAlreadyRemoved',
};

/**
 * Retiring or reopening one of the clinic's own codes.
 *
 * IT WRITES IMMEDIATELY, while everything else on this sheet is staged until save — the one place
 * this surface mixes two write models, and the confirm copy says so. It cannot be staged: a
 * retirement belongs to the CODE, which is a different record from the sheet, and holding it back
 * until the sheet saves would make "retire this code" silently conditional on an unrelated action.
 * The alternative — a separate "our codes" screen — is a bigger surface than this bet, and the
 * design puts the action here.
 */
const retireCode = (serviceCodeId) => {
  const serviceCode = codes.codeById(serviceCodeId);

  if (serviceCode === null) {
    return;
  }
  openComponentDialog(
    RetireServiceCodeDialog,
    { code: serviceCode.code, retiredOn: serviceCode.retiredOn, validFrom: serviceCode.validFrom },
    async (retiredOn) => {
      try {
        await codes.retire({ serviceCodeId, clinicId: props.clinicId, retiredOn });
        notifyInfo(i18n.t(retiredOn === null
          ? 'settings.billing.chargeMaster.retire.notifyReopened'
          : 'settings.billing.chargeMaster.retire.notifyRetired', { code: serviceCode.code }));
      } catch (error) {
        notifyNegative(i18n.t(
          RETIRE_REJECTION_KEY[error?.reason] ?? 'settings.billing.chargeMaster.retire.notifyFailed',
          { code: serviceCode.code, date: formatCalendarDate(serviceCode.validFrom) },
        ));
        console.error('Failed to change a service code retirement', error);
      }
    },
  );
};

/**
 * Removing a code the clinic authored by mistake — a tombstone, and the line goes with it.
 *
 * THE TWO HAPPEN TOGETHER, and neither makes sense alone. A code removed while its line stayed would
 * leave a row pointing at a record that has been disowned; a line dropped while the code stayed
 * would leave the code stranded, since the moment this dialog closes it stops being session-minted
 * and can never be removed again. So one action does both, and the confirmation says both.
 *
 * IT CONFIRMS, unlike removing a line. Removing a line stages a change that the save can still
 * undo; this writes to storage now and cannot be undone at all, which is the difference that earns
 * the interruption.
 */
const removeCode = (serviceCodeId) => {
  const serviceCode = codes.codeById(serviceCodeId);

  if (serviceCode === null || removalRejectionFor(serviceCodeId) !== null) {
    return;
  }
  confirmDestructiveDialog(
    i18n.t('settings.billing.chargeMaster.removeCode.title', { code: serviceCode.code }),
    i18n.t('settings.billing.chargeMaster.removeCode.textConfirm'),
    async () => {
      try {
        // FORWARDED, NOT ASSERTED. The early return above already refuses anything the rule would,
        // so hard-coding `true` here is safe today and only today — the two would diverge the moment
        // that guard changed, and the domain rule would then be judging a value this caller made up.
        await codes.remove({
          serviceCodeId,
          clinicId: props.clinicId,
          isSessionMinted: sessionMintedCodeIds.value.includes(serviceCodeId),
          hasChargeMasterReference: committedCodeIds.value.has(serviceCodeId),
        });
        draftLines.value = draftLines.value.filter((line) => line.serviceCodeId !== serviceCodeId);
        sessionMintedCodeIds.value = sessionMintedCodeIds.value.filter(
          (id) => id !== serviceCodeId,
        );
        notifyInfo(i18n.t('settings.billing.chargeMaster.removeCode.notifyRemoved', {
          code: serviceCode.code,
        }));
      } catch (error) {
        notifyNegative(i18n.t('settings.billing.chargeMaster.removeCode.notifyFailed'));
        console.error('Failed to remove an org-authored service code', error);
      }
    },
    { confirmLabel: i18n.t('settings.billing.chargeMaster.removeCode.buttonConfirm') },
  );
};

/**
 * The row's ⋯ menu, grouped by what each action acts ON.
 *
 * THE GROUPING IS THE WHOLE REASON THIS IS A MENU rather than the two icon buttons it replaces.
 * "Remove from charge master" and "Delete this code" read as the same action and are not remotely
 * the same: one drops a line from a staged sheet and leaves the code untouched; the other disowns a
 * record the clinic authored. A subheader naming the scope — this charge line, versus our code
 * XXXX — is what keeps somebody from reaching for the wrong one.
 *
 * THE CODE GROUP APPEARS ONLY ON A CODE THE CLINIC OWNS. Ours are not theirs to retire: a code's
 * industry lifespan reaches every clinic at once, and offering the action against a managed code
 * would promise something the rules refuse.
 *
 * A REFUSED DELETE STAYS VISIBLE AND SAYS WHY, in the trailing text. Hiding it sends the same person
 * looking for it again; enabling it promises an action that cannot happen.
 */
const rowMenuOptions = (row) => {
  const lineActions = row.isPendingRemoval
    ? [{
      label: i18n.t('settings.billing.chargeMaster.buttonRestoreLine'),
      icon: 'restore',
      action: () => restoreLine(row.serviceCodeId),
    }]
    : [{
      label: i18n.t(row.origin === ORIGIN.SESSION
        ? 'settings.billing.chargeMaster.buttonDiscardLine'
        : 'settings.billing.chargeMaster.buttonRemoveLine'),
      icon: 'remove',
      action: () => removeLine(row.serviceCodeId),
    }];
  const entries = [{
    subHeader: i18n.t('settings.billing.chargeMaster.menu.subheadThisLine'),
    options: lineActions,
  }];

  if (!row.isOwnCode) {
    return entries;
  }
  const rejection = removalRejectionFor(row.serviceCodeId);

  return entries.concat([{
    subHeader: i18n.t('settings.billing.chargeMaster.menu.subheadYourCode', { code: row.code }),
    options: [
      {
        label: i18n.t(row.hasRetirementBooked
          ? 'settings.billing.chargeMaster.menu.buttonReopenCode'
          : 'settings.billing.chargeMaster.menu.buttonRetireCode'),
        icon: 'event_busy',
        action: () => retireCode(row.serviceCodeId),
      },
      {
        label: i18n.t('settings.billing.chargeMaster.menu.buttonDeleteCode'),
        icon: 'delete',
        destructive: true,
        disable: rejection !== null,
        // Two wordings, not four. "It is already in use" is the case a person can act on — retire
        // it instead — where a managed or already-removed code is a state the menu should not have
        // offered at all, so the rest share one honest sentence rather than each inventing a reason.
        trailingText: rejection === null ? null : i18n.t(CANNOT_DELETE_KEY[rejection]
          ?? 'settings.billing.chargeMaster.menu.cannotDeleteOther'),
        action: () => removeCode(row.serviceCodeId),
      },
    ],
  }]);
};

/**
 * Closing without saving: disown whatever this session minted and never committed.
 *
 * WITHOUT THIS, EVERY ABANDONED MINT IS PERMANENT. A code is created the moment the mint form is
 * confirmed, before any sheet is saved, and it stops being removable the moment this dialog closes —
 * so a user who mints a code, thinks better of it and hits Cancel would leave a record in the
 * clinic's list that can only ever be retired. That is the sharp edge of gating removal on the
 * session, and this is where it gets blunted.
 *
 * FAILURES ARE LOGGED AND SWALLOWED. The user has asked to close; a toast about cleanup they did not
 * ask for explains nothing they can act on, and a rejection escaping here would stop the dialog
 * closing at all. What it leaves behind is a stranded code, which is the same outcome as not trying.
 *
 * IT CANNOT CATCH A CLOSED TAB, and nothing here can. A browser closed between minting and saving
 * strands the code exactly as before.
 */
const discardSessionMints = async () => {
  const stranded = sessionMintedCodeIds.value.filter(
    (serviceCodeId) => !committedCodeIds.value.has(serviceCodeId),
  );

  sessionMintedCodeIds.value = [];
  await Promise.all(stranded.map((serviceCodeId) => codes.remove({
    serviceCodeId,
    clinicId: props.clinicId,
    isSessionMinted: true,
    hasChargeMasterReference: false,
  }).catch((error) => {
    console.error('Failed to discard an abandoned org-authored code', error);
  })));
};

const onSubmit = async () => {
  if (effectiveFrom.value === null) {
    return;
  }
  // REFUSED, NOT SILENTLY DROPPED. Saving here would convert unreadable text to null and report the
  // row as "needs a charge", losing a number the user typed. The button stays ENABLED and explains
  // itself rather than going disabled: a dead primary action tells a keyboard or screen-reader user
  // nothing about why.
  if (invalidCharges.value.length > 0) {
    notifyNegative(invalidWarning.value);
    return;
  }
  isSaving.value = true;
  try {
    await chargeMaster.commit({
      clinicId: props.clinicId,
      effectiveFrom: effectiveFrom.value,
      record: { currency: draftCurrency.value, lines: linesToSave.value },
    });
    // COMMITTED, SO NO LONGER STRANDED. A code this session minted is now referenced by a saved
    // sheet, which is both what makes it un-removable and what makes it worth keeping — clearing the
    // list before the close handler runs is what stops the cleanup tombstoning a code the user has
    // just successfully priced. A marked line is the one case that does not survive: it was dropped
    // from `linesToSave`, so its code is genuinely unreferenced, and the cleanup is right to take it.
    sessionMintedCodeIds.value = sessionMintedCodeIds.value.filter(
      (serviceCodeId) => !linesToSave.value.some((line) => line.serviceCodeId === serviceCodeId),
    );
    emit('ok');
    hide();
  } catch (error) {
    // THE FAILURES WE EXPECT GET THEIR OWN WORDING; anything else is unexpected and is never shown
    // raw. A conflict is recoverable and says so — somebody else moved the timeline while this
    // dialog was open — where a currency change is a refusal the user has to understand rather than
    // retry.
    notifyNegative(i18n.t(`settings.billing.chargeMaster.${{
      EffectiveDateRejected: 'notifySaveConflict',
      VersionCollision: 'notifySaveConflict',
      CurrencyChangeRejected: 'notifyCurrencyLocked',
      AmbiguousChargeMaster: 'warningAmbiguous',
    }[error?.name] ?? 'notifySaveFailed'}`));

    // Re-read so the timeline the user is looking at matches what storage now says — a conflict
    // means it moved underneath them, and offering the same stale choices again would just fail.
    //
    // ITS OWN FAILURE IS SWALLOWED, not reported. The user has just been told the save failed, and a
    // second toast about the re-read that followed it explains nothing they can act on. It must not
    // escape either: this runs in a catch, so a rejection here would replace a handled save failure
    // with an unhandled one and leave the draft half-refreshed.
    try {
      await chargeMaster.load({ clinicId: props.clinicId });
      loadDraft();
    } catch (reloadError) {
      console.error('Failed to re-read the charge master after a failed save', reloadError);
    }
  } finally {
    isSaving.value = false;
  }
};

defineExpose({
  show: () => dialog.value?.show(),
  hide,
});
</script>

<template>
  <hr-versioned-form-dialog
    ref="dialog"
    v-model:option="saveChoice"
    v-model:date="pickedDate"
    :title="i18n.t('settings.billing.chargeMaster.editTitle')"
    :when-title="i18n.t('settings.billing.chargeMaster.versions.whenTitle')"
    size="lg"
    :effective-from-label="versionLine.effectiveFromLabel"
    :version-label="versionLine.versionLabel"
    :version-options="versionLine.versionOptions"
    :save-options="saveChoices"
    :min-date="presentMinDate(chargeMaster.timeline.value, chargeMaster.asOf)"
    :loading="isSaving"
    data-e2e="dialog-edit-charge-master"
    @submit="onSubmit"
    @close="discardSessionMints().finally(() => emit('hide'))"
  >
    <template
      v-if="replacementWarning !== null || unchargedWarning !== null || invalidWarning !== null"
      #warning
    >
      <hr-warning-banner
        v-if="invalidWarning !== null"
        :message="invalidWarning"
      />
      <hr-warning-banner
        v-if="replacementWarning !== null"
        :message="replacementWarning"
      />
      <hr-warning-banner
        v-if="unchargedWarning !== null"
        :message="unchargedWarning"
      />
    </template>

    <hr-warning-banner
      v-if="chargeMaster.isAmbiguous.value"
      :message="i18n.t('settings.billing.chargeMaster.warningAmbiguous')"
    />

    <!--
      SKELETON ROWS RATHER THAN A SPINNER. They show the shape of what is arriving, so the dialog does
      not resize under the reader when it lands, and a list of rows reads as "rows are coming" where a
      centerd spinner reads only as "wait".

      `HrListItem` has no pending state of its own, so the row is composed: its default slot replaces
      the label and caption, and `HrP` carries the skeleton. Nothing here reaches past the published
      interface — `HrSkeleton` itself is internal, and this is the sanctioned way to the same effect.

      Three rows because it suggests a list without implying a count, and ARIA-HIDDEN because they
      carry no information — a screen reader walking three empty rows learns nothing and loses its
      place. The dialog announces its own busy state, which is the part that has to be perceivable.
    -->
    <hr-list v-else-if="isLoading">
      <hr-list-item
        v-for="placeholder in SKELETON_ROWS"
        :key="placeholder"
        aria-hidden="true"
        data-e2e="row-charge-line-loading"
      >
        <hr-p
          pending
          :text="SKELETON_CODE_WIDTH"
        />
        <hr-p
          pending
          :text="SKELETON_DESCRIPTOR_WIDTH"
        />
        <template #trailing>
          <hr-currency-field pending />
        </template>
      </hr-list-item>
    </hr-list>

    <hr-list
      v-else
      ref="listEl"
    >
      <!--
        THE SUBHEAD CARRIES THE COUNTS, which is where a header row would have gone. An unpriced line
        far down a long sheet is otherwise only discoverable by scrolling to it — the count says how
        many there are before the scroll, and the save warning names them.
      -->
      <hr-list-section-header>
        {{ i18n.t('settings.billing.chargeMaster.subheadCodes', rows.length, { count: rows.length }) }}
        <template #trailing>
          <!--
            NEUTRAL, NOT WARNING-COLORED. The row states its own condition now, so the aggregate sits
            BELOW it in salience — it is navigational, telling somebody how many rows are affected when
            the rows themselves may be below the fold, and it disappears at zero.
          -->
          <span
            v-if="willHoldAtBilling.length > 0"
            data-e2e="text-unpriced-count"
          >
            {{ i18n.t('settings.billing.chargeMaster.subheadUnpriced', { count: willHoldAtBilling.length }) }}
          </span>
          <span
            v-if="pendingRemovalCodeIds.length > 0"
            class="q-ml-sm"
            data-e2e="text-removed-count"
          >
            {{ i18n.t('settings.billing.chargeMaster.subheadRemoved', { count: pendingRemovalCodeIds.length }) }}
          </span>
        </template>
      </hr-list-section-header>

      <!--
        TWO-LINE ROW, ONE TRAILING GROUP OF TWO. The code is the headline because it is what billing
        staff scan on; the descriptor is confirmatory and sits as supporting text. The unit basis is
        NOT a third trailing element — it lives inside the charge field as a suffix, so "€120.00
        /15 min" reads as one rate statement instead of two facts to join.

        A MARKED ROW STAYS, struck through and disabled. That is what shows the diff against the
        sheet in force while the user is still deciding, where a row that vanished would show the
        opposite. It leaves on save, not on click.
      -->
      <!--
        THE ROW IS A `label`, NOT A CLICKABLE ITEM. Both give click-anywhere-to-focus, but `clickable`
        makes the row focusable and keyboard-activatable: a tab stop per row, which breaks the
        Enter/Tab flow through the charge fields, and a button-ish announcement wrapped around a
        field. A label costs neither — it is not focusable, it adds no tab stop, and the charge input
        is associated implicitly by being inside it, so no id wiring is needed.
        It also makes the code and descriptor the field's accessible name, so the input announces as
        "0373T, behavior treatment…" rather than as an unlabeled box.
      -->
      <hr-list-item
        v-for="row in rows"
        :key="row.serviceCodeId"
        tag="label"
        :label="row.code ?? i18n.t('settings.billing.chargeMaster.textUnknownCode')"
        :caption="chargeLineCaption(row)"
        :active="focusedCodeId === row.serviceCodeId || null"
        :class="row.isPendingRemoval ? 'charge-line--pending-removal' : undefined"
        :data-e2e="`row-charge-line-${row.code ?? row.serviceCodeId}`"
      >
        <template #trailing>
          <div class="row items-center no-wrap q-gutter-sm">
            <hr-currency-field
              :model-value="chargeText[row.serviceCodeId] ?? ''"
              :currency="draftCurrency"
              :suffix="row.unitBasis ? i18n.t(`unitBasesShort.${row.unitBasis}`) : null"
              :placeholder="row.hasCharge ? null : i18n.t('settings.billing.chargeMaster.placeholderNeedsCharge')"
              :hint="chargeHint(row)"
              :hide-bottom-space="chargeHint(row) === null"
              :disable="row.isPendingRemoval"
              dense
              :data-charge-field="row.serviceCodeId"
              :data-e2e="`input-charge-${row.code ?? row.serviceCodeId}`"
              @update:model-value="setCharge(row.serviceCodeId, $event)"
              @focus="focusedCodeId = row.serviceCodeId"
              @blur="onChargeBlur(row.serviceCodeId)"
              @keydown.enter.prevent="focusNextCharge(row.serviceCodeId)"
            />
            <!--
              A ⋯ MENU RATHER THAN THE BARE MINUS THIS ROW USED TO CARRY, and the reason is the
              second group inside it. A code the clinic authored can be retired or removed, and both
              of those act on the CODE where the minus acts on the LINE — three actions that read
              alike and are not alike. A menu can say which is which with a subheader; a row of icons
              cannot, and a third icon beside two others would be the least legible way to offer the
              most consequential action on the screen.

              ONE CONTROL ON EVERY ROW, including rows with only the line group. A menu that appeared
              only on the clinic's own codes would move the remove action depending on who authored
              the code, which is a fact the reader has no reason to be tracking.
            -->
            <hr-overflow-menu-btn
              :options="rowMenuOptions(row)"
              :aria-label="i18n.t('settings.billing.chargeMaster.menu.buttonRowActions', {
                code: row.code ?? row.serviceCodeId,
              })"
              :data-e2e="`menu-charge-line-${row.code ?? row.serviceCodeId}`"
              @click.stop
            />
          </div>
        </template>
      </hr-list-item>

      <!--
        Inline as the last row rather than a detached button: it reads as part of the collection and
        sits where the eye already is after the last line.
      -->
      <hr-list-add-btn
        :label="i18n.t('settings.billing.chargeMaster.buttonAddCode')"
        data-e2e="button-add-service-code"
        @click="addCode"
      />
    </hr-list>
  </hr-versioned-form-dialog>
</template>

<style scoped>
/*
 * A MARKED ROW READS AS LEAVING, not as broken. Dimmed and struck through — the treatment for
 * something on its way out — and deliberately NOT the error role, which is already carrying the
 * unpriced warning on the same row and would be blunted by sharing it with a valid, deliberate,
 * uncommitted action.
 *
 * The row stays interactive in one respect only: its restore control. The charge field is disabled
 * rather than hidden, so the amount is still legible while the user decides — restoring returns
 * exactly what is on screen.
 */
.charge-line--pending-removal {
  opacity: 0.6;
}

.charge-line--pending-removal :deep(.q-item__label) {
  text-decoration: line-through;
}
</style>
